import { createConsumer } from '@rails/actioncable';
import { getChannelAccessToken } from './api-helper';
import notification from '../components/notification';
import { AppConfig } from './config';

let cable = null;
let cablePromise = null;
let isReconnecting = false;
let reconnectTimeoutId = null;
let reconnectAttempts = 0;

const RECONNECT_DELAYS = { base: 2000, max: 30000, maxAttempts: 50 };
const subscriptions = new Map();

const getDelay = () =>
  Math.min(
    RECONNECT_DELAYS.base * Math.pow(1.5, reconnectAttempts),
    RECONNECT_DELAYS.max
  );

const disconnectCable = () => {
  if (cable) {
    try {
      cable.disconnect();
    } catch {
      /* ignore */
    }
    cable = null;
  }
  cablePromise = null;
};

/**
 * Get or create cable with connection token (handles concurrent calls)
 */
const getOrCreateCable = async () => {
  if (cable) return cable;
  if (cablePromise) return cablePromise;

  cablePromise = (async () => {
    const tokenResp = await getChannelAccessToken({});
    cable = createConsumer(
      `${AppConfig.wsBaseUrl}?token=${tokenResp?.data?.token}`
    );
    return cable;
  })();

  return cablePromise;
};

const createSubscriptionHandlers = (entry) => ({
  received: entry.handlers.received,
  connected: () => {
    entry.handlers.connected?.();
    reconnectAttempts = 0;
  },
  disconnected: () => {
    entry.handlers.disconnected?.();
    if (!isReconnecting) scheduleReconnect();
  }
});

const scheduleReconnect = () => {
  if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
  disconnectCable();

  if (subscriptions.size === 0) return;

  reconnectAttempts++;

  if (reconnectAttempts >= RECONNECT_DELAYS.maxAttempts) {
    console.error('WebSocket: Max reconnection attempts reached');
    notification('error', 'Connection lost. Please refresh the page.');
    return;
  }

  const delay = getDelay();
  console.info(
    `WebSocket: Reconnect attempt ${reconnectAttempts} in ${delay}ms`
  );
  reconnectTimeoutId = setTimeout(reconnectAll, delay);
};

const reconnectAll = async () => {
  if (isReconnecting || subscriptions.size === 0) return;

  if (!navigator.onLine) {
    console.info('WebSocket: Offline, waiting...');
    scheduleReconnect();
    return;
  }

  isReconnecting = true;

  try {
    // Get fresh connection token and create cable
    const tokenResp = await getChannelAccessToken({});
    cable = createConsumer(
      `${AppConfig.wsBaseUrl}?token=${tokenResp?.data?.token}`
    );

    for (const [key, entry] of subscriptions.entries()) {
      if (!subscriptions.has(key)) continue;
      const channelToken = await getChannelAccessToken(entry.channelParams);
      entry.subscription = cable.subscriptions.create(
        channelToken?.data,
        createSubscriptionHandlers(entry)
      );
    }
  } catch (error) {
    console.error('WebSocket: Reconnection failed:', error);
    disconnectCable();
    isReconnecting = false;
    scheduleReconnect();
    return;
  }

  isReconnecting = false;
};

/**
 * Subscribe to a WebSocket channel
 * @param {Object} channelParams - { entityType, entityId } for getChannelAccessToken
 * @param {Object} handlers - { received, connected, disconnected }
 * @returns {Object} subscription object with unsubscribe method
 */
export const wsSubscribe = async (channelParams, handlers) => {
  const key = `${channelParams.entityType}:${channelParams.entityId}`;

  if (subscriptions.has(key)) wsUnsubscribe(key);

  try {
    const activeCable = await getOrCreateCable();
    const channelToken = await getChannelAccessToken(channelParams);

    const entry = { channelParams, handlers, subscription: null };
    entry.subscription = activeCable.subscriptions.create(
      channelToken?.data,
      createSubscriptionHandlers(entry)
    );
    subscriptions.set(key, entry);

    return { key, unsubscribe: () => wsUnsubscribe(key) };
  } catch (error) {
    notification(
      'error',
      error?.response?.data?.error || 'Unable to connect to socket!'
    );
    return null;
  }
};

/**
 * Unsubscribe from a WebSocket channel
 * @param {string} key - subscription key
 */
export const wsUnsubscribe = (key) => {
  const entry = subscriptions.get(key);
  if (entry?.subscription) {
    try {
      entry.subscription.unsubscribe();
    } catch {
      /* ignore */
    }
  }
  subscriptions.delete(key);
};

/**
 * Disconnect cable entirely (call on app unmount if needed)
 */
export const wsDisconnect = () => {
  if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
  reconnectAttempts = 0;
  isReconnecting = false;

  subscriptions.forEach((entry) => {
    try {
      entry.subscription?.unsubscribe();
    } catch {
      /* ignore */
    }
  });
  subscriptions.clear();
  disconnectCable();
};

/**
 * Check if cable is connected
 */
export const wsIsConnected = () => cable && !cable.connection.disconnected;
